import { resolveEffectiveAuthChain, normalizeAuth, encodeBasicCredentials } from '../requestAuth';
import { createVariableResolver } from '../resolveVariables';

// ─── Secret variable redaction ─────────────────────────────────────────────

export const SECRET_REDACTED = '<SECRET_REDACTED>';

/**
 * Build a substituteVariables function that replaces secret variable values
 * with <SECRET_REDACTED> in generated code snippets, preventing accidental
 * exposure of real API keys and tokens when sharing code.
 *
 * @param {Array<{key: string, value: string, secret?: boolean, enabled?: boolean}>} variables
 * @returns {(s: string) => string} substituteVariables function
 */
export function buildRedactingSubstitute(variables, { revealSecrets = false } = {}) {
  const map = new Map();
  for (const v of variables || []) {
    if (v.enabled !== false && v.key) {
      // Redaction is the default and stays the default: a snippet is made to
      // be pasted into a chat, an issue or a README, and a live token that
      // travels with it is the most ordinary way credentials leak.
      //
      // `revealSecrets` is the user saying, per generation, that this one is
      // for their own terminal. Their call to make — but it has to be a call,
      // not the behaviour they get without asking.
      const reveal = revealSecrets || !v.secret;
      map.set(v.key, reveal ? v.value ?? '' : SECRET_REDACTED);
    }
  }
  if (!map.size) return null;
  // Shared resolver, so a variable whose value contains another reference
  // resolves in a generated snippet exactly as it does in a real request.
  return createVariableResolver((name) => (map.has(name) ? map.get(name) : null));
}

// ─── Helpers ───────────────────────────────────────────────────────────────

export function encodeCodeParam(str) {
  return str
    .replace(/%(?![0-9A-Fa-f]{2})/g, '%25')
    .replace(/ /g, '%20')
    .replace(/&/g, '%26')
    .replace(/#/g, '%23');
}

export function stripQuery(url) {
  const q = (url || '').indexOf('?');
  return q === -1 ? (url || '') : url.slice(0, q);
}

export function buildUrlWithParams(url, params) {
  const enabled = (params || []).filter((p) => p.enabled !== false && p.key);
  if (!enabled.length) return url || '';
  // Strip query from the stored URL — params table is the source of truth.
  const base = stripQuery(url);
  const qs = enabled
    .map((p) => {
      const k = encodeCodeParam(p.key);
      const v = encodeCodeParam(p.value || '');
      return v ? `${k}=${v}` : k;
    })
    .join('&');
  return `${base}?${qs}`;
}

export function buildHeadersMap(headers) {
  const map = {};
  for (const h of headers || []) {
    if (h.enabled !== false && h.key) {
      map[h.key] = h.value ?? '';
    }
  }
  return map;
}

export function buildCookieString(cookies) {
  const enabled = (cookies || []).filter((c) => c.enabled !== false && c.key);
  if (!enabled.length) return null;
  return enabled.map((c) => `${c.key}=${c.value || ''}`).join('; ');
}

export function hasTemplateVars(...values) {
  return values.some((v) => typeof v === 'string' && /\{\{.+?\}\}/.test(v));
}

export function partsHaveVars(parts) {
  const headerVals = Object.entries(parts.headers).flatMap(([k, v]) => [k, v]);
  return hasTemplateVars(parts.url, parts.body || '', ...headerVals);
}

export function needsBody(method, body) {
  if (!body) return false;
  return !['GET', 'HEAD', 'OPTIONS'].includes(method);
}

export function tryParseFormData(body) {
  if (!body) return null;
  try {
    const parsed = JSON.parse(body);
    if (Array.isArray(parsed) && parsed.length > 0 && 'key' in parsed[0]) {
      return parsed.filter((f) => f.enabled !== false && f.key);
    }
  } catch {
    // not JSON form fields
  }
  return null;
}

// ─── Public ────────────────────────────────────────────────────────────────

/**
 * @param {object} request
 * @param {object|null} collectionAuth
 * @param {Array|null} variables - optional env variable list; if provided, secret
 *   variable values are replaced with <SECRET_REDACTED> in generated code to prevent
 *   accidental exposure when sharing snippets.
 * @param {{revealSecrets?: boolean, folderAuthChain?: Array<object>}} [options]
 *   `folderAuthChain` (nearest folder first) lets an `inherit` request pick up
 *   its folder's auth, exactly as Send and Run collection do.
 */
export function buildRequestParts(request, collectionAuth, variables = null, options = {}) {
  const substituteVariables = variables ? buildRedactingSubstitute(variables, options) : null;
  const url = buildUrlWithParams(request.url, request.params);
  const headersMap = buildHeadersMap(request.headers);
  const effectiveAuth = resolveEffectiveAuthChain(request.auth, options.folderAuthChain || [], collectionAuth);
  const normalizedAuth = normalizeAuth(effectiveAuth, { forRequest: false });

  let headers = { ...headersMap };
  let finalUrl = url;

  if (normalizedAuth.type === 'awssigv4') {
    // AWS SigV4 signing is time-dependent and must not expose the secret key in
    // generated code. Add placeholder headers so the snippet shows where auth goes.
    headers['Authorization'] = '/* AWS4-HMAC-SHA256 — sign at runtime */';
    headers['X-Amz-Date'] = '/* <YYYYMMDDTHHMMSSZ> */';
  } else {
    // For all other auth types, use the synchronous path (no awaiting needed).
    const result = applySyncAuth({ auth: normalizedAuth, headers: headersMap, url, substituteVariables });
    headers = result.headers;
    finalUrl = result.url;
  }

  const cookieStr = buildCookieString(request.cookies);
  if (cookieStr) headers['Cookie'] = cookieStr;

  // Apply variable substitution to body and URL if substituteVariables is available
  const resolvedUrl = substituteVariables ? substituteVariables(finalUrl) : finalUrl;
  const resolvedBody = substituteVariables ? substituteVariables(request.body || '') : (request.body || '');

  return {
    method: (request.method || 'GET').toUpperCase(),
    url: resolvedUrl,
    headers,
    body: resolvedBody,
    body_type: request.body_type || 'raw',
  };
}

/**
 * Synchronous auth application used only by code generators (no SigV4).
 * Mirrors applyRequestAuth logic but stays synchronous for code-gen compatibility.
 */
export function applySyncAuth({ auth, headers, url, substituteVariables }) {
  const nextHeaders = { ...headers };
  let nextUrl = url || '';

  function sub(value) {
    if (substituteVariables && value != null) return substituteVariables(String(value));
    return value == null ? '' : String(value);
  }

  function appendQueryParam(u, key, value) {
    if (!key) return u;
    const encoded = `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    if (!u) return `?${encoded}`;
    if (u.includes('?')) {
      return u.endsWith('?') || u.endsWith('&') ? `${u}${encoded}` : `${u}&${encoded}`;
    }
    return `${u}?${encoded}`;
  }

  switch (auth.type) {
    case 'bearer': {
      const token = sub(auth.bearer).trim();
      if (token) nextHeaders.Authorization = `Bearer ${token}`;
      break;
    }
    case 'apikey': {
      const key = sub(auth.apikey?.key).trim();
      const value = sub(auth.apikey?.value);
      if (key) {
        if (auth.apikey?.in === 'query') {
          nextUrl = appendQueryParam(nextUrl, key, value);
        } else {
          nextHeaders[key] = value;
        }
      }
      break;
    }
    case 'basic': {
      const username = sub(auth.basic?.username);
      const password = sub(auth.basic?.password);
      if (username || password) {
        // A {{name}} nothing resolved, or a redacted secret, is left READABLE:
        // base64 would hide it inside a credential that looks real and is wrong,
        // and the "Replace {{...}} placeholders" note would never appear.
        const placeholder = [username, password].some(
          (v) => /\{\{.+?\}\}/.test(v) || v.includes(SECRET_REDACTED)
        );
        nextHeaders.Authorization = placeholder
          ? `Basic <base64(${username}:${password})>`
          : `Basic ${encodeBasicCredentials(username, password)}`;
      }
      break;
    }
    case 'oauth2': {
      const token = sub(auth.oauth2?.accessToken).trim();
      const tokenType = sub(auth.oauth2?.tokenType).trim() || 'Bearer';
      if (token) nextHeaders.Authorization = `${tokenType} ${token}`;
      break;
    }
    default:
      break;
  }

  return { headers: nextHeaders, url: nextUrl };
}
